import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import guestRequestApi from '../../services/guestRequestApi';
import BookingCheckinMessagesSection from '../BookingCheckinMessagesSection';
import PreBookingContextLine from './PreBookingContextLine';
import UnitStatusChip from '../UnitStatusChip';
import OpenComplaintButton from '../complaints/OpenComplaintButton';
import {
  ARRIVAL_VIEW,
  ARRIVAL_REASON_AR,
  ARRIVAL_REASON_UNKNOWN,
  ARRIVAL_UNAVAILABLE_SHORT,
  ARRIVAL_NO_EVIDENCE_HINT,
  ARRIVAL_ERROR_AR
} from '../../config/arrivalLabels';
import {
  GR_TYPE_LABELS,
  GR_STATUS_LABELS,
  GR_PRIORITY_LABELS
} from '../../config/guestRequestLabels';
import { formatRelativeTime } from '../../utils/dateFormatter';
import { formatCount } from '../../utils/numberFormatter';

// Guest Comms Inc 7 — the context line beside an open conversation.
// Spec: tasks/guest-comms-inc7-context-panel.md · governing decision D95.
//
// TWO FACTS ON ONE LINE: has the guest actually got in (from real smart-lock usage), and what is
// their door code. Behind a tap: open guest requests, and what we have already auto-sent them.
// That is the whole component. Four items that used to be here — unit, building, stay dates,
// booking reference — were dropped because the thread header renders all four a few pixels away.
//
// ⚠️ THE ARRIVAL ANSWER IS NOT COMPUTED HERE, and must never be. It comes from the one shared
// resolver (be/services/guestArrival/resolver.js) that the booking-details page also reads. This
// component renders a verdict; it does not form one. Its WORDS come from config/arrivalLabels.js
// for the same reason — agreeing on the data while retyping the sentences is the drift that would
// never show up in a diff.
//
// What each surface DOES own is its own presentation: the booking page renders a card badge, this
// renders a one-line dot. So the shared map's `label` is used and its `cls` deliberately is not.
//
// 🎨 NO_EVIDENCE STAYS NEUTRAL — never amber, never red. A warning colour asserts "the guest failed
// to arrive", the exact claim this feature exists not to make: shared building codes are the
// highest-traffic entries on the fleet and belong to no booking. Only ARRIVED earns colour, and it
// borrows the SAME green the list row already uses for «مستضيف حالياً» — one colour, one meaning
// (the guest is physically here) across two surfaces.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// ONE PANEL, TWO SHAPES (pre-booking increment 4). The endpoint serves a second block for a thread
// with NO booking — is the unit free, is it clean, when does the door open — and this component is
// still the only panel the inbox mounts: it dispatches on `kind`, which the API sets on the
// pre-booking shape ONLY (the booked response was byte-identical to what it was before that work, so
// the discriminator could not be symmetric — it has since gained `unitStatus`, tasks/unit-operational-status.md).
//
// The two shapes share nothing but this wrapper, and everything above this line is booking-only —
// the guest-requests fetch, its cancel ref and its lazy toggle have no counterpart on an enquiry,
// where the whole payload arrives in one response. That is why the pre-booking body is its own file
// rather than a second early-return underneath a hook chain it never uses.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The one-line summary of arrival, and the dot's tone.
 *
 * `tone` is presentation only. It is derived from `status`, never from `reason`: a reason explains
 * why we could not look, and no explanation should be able to change a neutral state into a
 * coloured one.
 */
const arrivalTone = (status) => {
  if (status === 'ARRIVED') return 'is-arrived';
  if (status === 'NO_EVIDENCE') return 'is-none';
  return 'is-unknown';
};

const ThreadContextPanel = ({ context, loading, error, conversationId }) => {
  const [expanded, setExpanded] = useState(false);
  const [requests, setRequests] = useState(null);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState(false);

  const bookingId = context?.bookingId || null;

  // WHICH BOOKING THE IN-FLIGHT REQUEST FETCH BELONGS TO.
  //
  // This component is NOT unmounted when the operator switches threads — the thread pane keeps
  // rendering, so the fiber and all of this state survive. Without this ref: expand thread A, click
  // thread B before A's requests land, and A's list arrives into B's panel. Worse than a flash — it
  // STICKS, because the toggle's "already loaded" check then sees a non-null array and refuses to
  // fetch B's. The parent guards its own fetch; this one had nothing.
  const requestsForRef = useRef(null);

  // Collapse and forget when the thread changes. Without this, opening a second conversation would
  // inherit the first one's expanded state and immediately fetch its requests — work nobody asked
  // for, on a panel whose entire design constraint is not being in the way.
  useEffect(() => {
    setExpanded(false);
    setRequests(null);
    setRequestsError(false);
    // Reset too, or a switch made while a fetch was in flight leaves the toggle believing a load is
    // still running and skipping the new booking's fetch entirely.
    setRequestsLoading(false);
    requestsForRef.current = null;
  }, [bookingId]);

  /**
   * LAZY, and only once per thread. Nothing behind the tap is fetched until someone taps — the
   * point of an expandable here is cost and clutter, and pre-fetching would forfeit both.
   *
   * The automated-sends half fetches itself: BookingCheckinMessagesSection is mounted only when
   * expanded, and owns its own request, spinner and failure. Two independent failures beat one
   * combined loader that hides whichever half did work.
   */
  const loadRequests = useCallback(async (id) => {
    requestsForRef.current = id;
    setRequestsLoading(true);
    setRequestsError(false);
    try {
      const data = await guestRequestApi.list({ bookingId: id });
      // The thread moved on while this was in flight. Drop it — every write below belongs to a
      // booking nobody is looking at any more.
      if (requestsForRef.current !== id) return;
      // The list endpoint returns every status; "open" is the client-side half of the filter. Safe
      // here and nowhere near the fetch-all-then-discard line — a booking has a handful of requests,
      // not a page of them (API_DATA_FETCHING_PATTERNS.md: client-side for UX on a small set).
      const open = (data.requests || []).filter(
        (r) => r.status !== 'completed' && r.status !== 'cancelled'
      );
      setRequests(open);
    } catch (err) {
      if (requestsForRef.current !== id) return;
      console.error('Failed to load guest requests for the context panel:', err);
      // A failure to load requests must never read as "there are none" — that is the same
      // lying-by-omission the arrival wording exists to prevent, one field over.
      setRequestsError(true);
    } finally {
      if (requestsForRef.current === id) setRequestsLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && bookingId && requests === null && !requestsLoading) loadRequests(bookingId);
  };

  // NOTHING TO SHOW IS NOT AN ERROR. An enquiry thread has no booking — roughly half of prod
  // conversations — and renders no panel at all rather than an empty shell explaining its own
  // absence. Same for a still-loading first paint: the thread must never wait on this, so an
  // in-flight panel is simply invisible.
  if (loading || (!context && !error)) return null;

  // A broken lookup says so, quietly and in one line. Silence would leave an operator reading a
  // thread with no idea the panel had failed rather than found nothing.
  if (error) {
    return (
      <div className="inbox-context inbox-context-error" role="status">
        <i className="bi bi-exclamation-triangle" aria-hidden="true" />
        <span>{ARRIVAL_ERROR_AR}</span>
      </div>
    );
  }

  // THE SECOND SHAPE. `kind` is present only here, so anything without it is the booked panel below
  // and the existing path is reached exactly as before.
  //
  // ⚠️ KEYED ON THE CONVERSATION, and that is load-bearing rather than idiomatic decoration. This
  // component is NOT unmounted when the operator switches threads, and `bookingId` — which the reset
  // effect above keys on — is null for EVERY enquiry thread, so nothing up there fires between two
  // enquiries. Without the key, expanding one enquiry's panel would leave the next one already open
  // showing a different unit's readiness. The key remounts the child instead, which resets its state
  // structurally and cannot be forgotten the next time state is added to it.
  if (context.kind === 'prebooking') {
    return <PreBookingContextLine key={conversationId} context={context} />;
  }

  const { accessCode, arrival, cancelled, unitStatus } = context;
  // `arrival: null` means the lookup itself FAILED while the door code survived — the endpoint
  // settles the two halves independently now, so the loudest guest question is still answered. That
  // is NOT the same as a NOT_APPLICABLE verdict, which is us successfully finding nothing to report,
  // so it must not borrow that verdict's wording.
  const arrivalFailed = !arrival;
  const view = ARRIVAL_VIEW[arrival?.status] || ARRIVAL_VIEW.NOT_APPLICABLE;
  const tone = arrivalTone(arrival?.status);
  const arrived = arrival?.status === 'ARRIVED';
  // Fall back to the same sentence the booking page uses. Without one an unmapped or future reason
  // leaves «غير معروف» standing alone inviting a guess — which is the state both this component and
  // the shared label module exist to prevent, and it was diverging at the render site rather than
  // in the vocabulary the module centralises.
  const reasonText = !arrivalFailed && !arrived
    ? (ARRIVAL_REASON_AR[arrival.reason] || ARRIVAL_REASON_UNKNOWN)
    : null;

  return (
    <div className="inbox-context">
      {/* NO `aria-label` HERE, deliberately. An aria-label REPLACES the element's contents in the
          accessible name, so labelling this «عرض تفاصيل الحجز» meant a screen-reader user heard the
          affordance and lost both facts the component exists to deliver — the arrival verdict and
          the door code. `aria-expanded` already carries the expand/collapse semantic, and
          `aria-controls` names the region, so the name is free to be the content. */}
      <button
        type="button"
        className="inbox-context-line"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls="inbox-context-body"
      >
        <span className={`inbox-context-arrival ${tone}`}>
          <span className="inbox-context-dot" aria-hidden="true" />
          {/* `standalone` where it exists: this line carries no title, so a bare «غير معروف» reads
              as "unknown WHAT?" beside a labelled door code. The other states name their own
              subject already and fall through to `label`. */}
          {arrivalFailed ? ARRIVAL_UNAVAILABLE_SHORT : (view.standalone || view.label)}
          {/* An Instant (the lock's own clock — see TEMPORAL_FIELD_CATALOGUE.md), so it goes through
              the instant-aware formatter. NEVER formatTime, which is for wall-clock HH:MM strings
              and would render the wrong hour outside Riyadh. */}
          {arrived && arrival.arrivedAt && (
            <time className="inbox-context-time">{formatRelativeTime(arrival.arrivedAt)}</time>
          )}
        </span>

        {/* The single loudest guest question on prod at 9% — three times parking. Tabular digits
            because a code is scanned and copied, not read as prose. Suppressed by the API on a
            cancelled stay, so an absent code here is never an accident. */}
        {/* «حالة التشغيل» of the unit the guest is in — the same resolver as the unit page (D2); the
            reason is in the chip's tooltip. Absent (null) when it could not be worked out. */}
        <UnitStatusChip unitStatus={unitStatus} />

        {accessCode && (
          <span className="inbox-context-code">
            <span className="inbox-context-code-label">الرمز</span>
            <bdi className="inbox-context-code-value">{accessCode}</bdi>
          </span>
        )}

        <i
          className={`bi bi-chevron-down inbox-context-chevron${expanded ? ' is-open' : ''}`}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div className="inbox-context-body" id="inbox-context-body">
          {/* The lookup failed rather than found nothing. Said in the same honesty register as the
              verdicts: a technical failure is never evidence about the guest either way. */}
          {arrivalFailed && <p className="inbox-context-hint">{ARRIVAL_ERROR_AR}</p>}

          {/* Absence is stated, then immediately qualified — an operator must never read "no record"
              as "no guest". Shown only where we genuinely looked and came up empty. */}
          {arrival?.status === 'NO_EVIDENCE' && (
            <p className="inbox-context-hint">{ARRIVAL_NO_EVIDENCE_HINT}</p>
          )}
          {/* Why we could not look. Kept out of the summary line so the state stays scannable and
              «غير معروف» never has to stand alone inviting a guess. */}
          {reasonText && <p className="inbox-context-hint">{reasonText}</p>}

          <section className="inbox-context-section">
            <h3 className="inbox-context-heading">
              طلبات الضيف المفتوحة
              {requests?.length > 0 && (
                <span className="inbox-context-count">{formatCount(requests.length)}</span>
              )}
            </h3>

            {requestsLoading && <Spinner animation="border" size="sm" />}

            {requestsError && (
              <p className="inbox-context-hint">تعذّر تحميل الطلبات. لا يعني ذلك عدم وجود طلبات.</p>
            )}

            {!requestsLoading && !requestsError && requests?.length === 0 && (
              <p className="inbox-context-hint">لا توجد طلبات مفتوحة لهذا الحجز.</p>
            )}

            {!requestsLoading && !requestsError && requests?.length > 0 && (
              <ul className="inbox-context-requests">
                {requests.map((r) => (
                  <li key={r._id} className="inbox-context-request">
                    <span className="inbox-context-request-type">
                      {GR_TYPE_LABELS[r.type] || GR_TYPE_LABELS.other}
                    </span>
                    <span className="inbox-context-request-status">
                      {GR_STATUS_LABELS[r.status] || r.status}
                    </span>
                    {r.priority === 'urgent' && (
                      <span className="inbox-context-request-urgent">
                        {GR_PRIORITY_LABELS.urgent}
                      </span>
                    )}
                    <time className="inbox-context-request-time">
                      {formatRelativeTime(r.createdAt)}
                    </time>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* A PROBLEM TO LEARN FROM, not a request to fulfil (tasks/complaints-to-fixes.md C1, C3): the
              guest's issue is still handled in this thread, and a guest request still covers "do it now".
              The complaint is what makes us fix it for good. The server reads the booking and the guest's
              latest words from this conversation — nothing is retyped. */}
          <section className="inbox-context-section">
            <h3 className="inbox-context-heading">الشكاوى</h3>
            {/* Keyed on the conversation: this panel is not unmounted between threads of the same booking,
                so without the key one thread's «فُتحت الشكوى» note would show on the next. */}
            <OpenComplaintButton key={conversationId} source={{ kind: 'message', id: conversationId }} />
          </section>

          {/* WHAT WE HAVE ALREADY SENT THEM — so nobody repeats the check-in message by hand.
              The booking page's own read-only section, reused rather than re-rendered: it already
              owns the four label maps (type, status, skip reason, error), fetches its own data on
              its own endpoint, and covers all four automated sends. A second implementation would
              have to retype every one of those strings.

              ⚠️ WITHHELD ON A CANCELLED STAY, and this is not cosmetic tidiness. The section renders
              each sent message's FULL TEXT, and the check-in message has the door code inside it —
              so the collapsed line suppressing the code while one tap printed it in full made the
              suppression a promise the panel did not keep. The send history still exists and is a
              legitimate record; it belongs on the booking page, which is the surface for reading
              history. This one is for answering a live guest. */}
          {cancelled ? (
            <section className="inbox-context-section">
              <p className="inbox-context-hint">
                الحجز ملغي — سجلّ الرسائل الآلية متاح في صفحة الحجز.
              </p>
            </section>
          ) : (
            <section className="inbox-context-section">
              {/* h3 here: it is nested under this panel's own h3 sections, and the page title is
                  already an h2. Its default h2 made the outline read h2 → h3 → h2 and rendered
                  this title visibly larger than the section above it. */}
              <BookingCheckinMessagesSection bookingId={bookingId} headingLevel="h3" />
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default ThreadContextPanel;
